import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import * as moment from 'moment';
import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { FlightsModel } from 'src/app/model/file/model.file';
import { FileService } from 'src/app/services/api/file.service';
import { FileProgrammingRepository } from 'src/entities/file';
import {
  FLIGHTS_DIALOG_COPY,
  FLIGHTS_DISPLAYED_COLUMNS,
  FLIGHTS_TABLE_HEADERS,
} from 'src/features/file-programming/flights-dialog/lib/flights-dialog-copy';

interface PassengerFlightLink {
  flightIds?: string[];
  passengerId?: string;
  Pax?: string;
  pasajero?: PassengerDetail;
}

interface PassengerDetail {
  id?: string;
  firstName?: string;
  lastName?: string;
}

interface EnrichedPassengerLink extends PassengerFlightLink {
  pasajero?: PassengerDetail;
}

interface SalesPassengerRow {
  Pax?: string;
  pasajero?: PassengerDetail;
}

interface PaxEntry {
  name: string;
  sortKey: string;
}

@Component({
  selector: 'app-vuelos-dialog',
  templateUrl: './vuelos-dialog.component.html',
  styleUrls: ['./vuelos-dialog.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class VuelosDialogComponent implements OnInit {
  readonly copy = FLIGHTS_DIALOG_COPY;
  readonly headers = FLIGHTS_TABLE_HEADERS;
  readonly displayedColumns = [...FLIGHTS_DISPLAYED_COLUMNS];

  isLoading = false;
  assignedPassengers: PassengerDetail[] = [];
  enrichedPassengers: EnrichedPassengerLink[] = [];
  salesPassengers: SalesPassengerRow[] = [];
  dataSource: FlightsModel[] | null = null;
  flightDataSource = new MatTableDataSource<FlightsModel>();
  selectedFlightId: string | null = null;
  private paxEntriesByFlightId = new Map<string, PaxEntry[]>();

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    if (sort) {
      this.flightDataSource.sort = sort;
    }
  }

  constructor(
    private readonly fileService: FileService,
    private readonly programmingRepository: FileProgrammingRepository,
    public dialogRef: MatDialogRef<VuelosDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public id: string,
    private readonly liveAnnouncer: LiveAnnouncer
  ) {}

  ngOnInit(): void {
    if (!this.id) {
      this.dataSource = [];
      this.flightDataSource.data = [];
      return;
    }

    this.isLoading = true;
    forkJoin({
      flights: this.fileService.getFlightsByItineraryId(this.id),
      passengerLinks: this.fileService.getItineraryPassengerFlightLinks(this.id),
      assignedPassengers: this.fileService
        .getAssignedPassengers({
          itineraryId: this.id,
          passengerFields: {
            id: true,
            firstName: true,
            lastName: true,
          },
        })
        .pipe(
          map((response) => this.extractAssignedPassengers(response)),
          catchError(() => of([] as PassengerDetail[]))
        ),
      salesPassengers: this.programmingRepository
        .getSalesPassengersByItinerary(this.id)
        .pipe(
          map((response) => this.extractSalesPassengers(response)),
          catchError(() => of([] as SalesPassengerRow[]))
        ),
    })
      .pipe(
        switchMap(({ flights, passengerLinks, assignedPassengers, salesPassengers }) => {
          const lstPaX = this.mergePassengerFlightLinks(
            passengerLinks?.getItinerary
          );
          const passengerIds = lstPaX
            .map((link) => link.passengerId)
            .filter((passengerId): passengerId is string => !!passengerId);

          const passengers$ =
            passengerIds.length > 0
              ? this.fileService
                  .getPassengersByIds({
                    passengerIds,
                    passengerFields: {
                      id: true,
                      firstName: true,
                      lastName: true,
                    },
                  })
                  .pipe(catchError(() => of({ getPassengers: [] as PassengerDetail[] })))
              : of({ getPassengers: [] as PassengerDetail[] });

          return passengers$.pipe(
            map((passengersRes) => {
              const passengerDetails: PassengerDetail[] =
                passengersRes?.getPassengers ?? [];

              return {
                flights,
                assignedPassengers,
                salesPassengers,
                enrichedPassengers: lstPaX.map((link) => ({
                  ...link,
                  pasajero:
                    this.findSalesPassengerDetail(link) ??
                    passengerDetails.find(
                      (passenger: PassengerDetail) =>
                        passenger.id === link.passengerId
                    ) ??
                    link.pasajero,
                })),
              };
            })
          );
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: ({ flights, assignedPassengers, salesPassengers, enrichedPassengers }) => {
          this.assignedPassengers = assignedPassengers;
          this.salesPassengers = salesPassengers;
          this.enrichedPassengers = enrichedPassengers;
          const flightsList = flights?.getFlightsByItineraryId ?? [];
          this.dataSource = flightsList;
          this.flightDataSource.data = flightsList;
          this.selectedFlightId = null;
          this.rebuildPaxCache();
        },
        error: () => {
          this.assignedPassengers = [];
          this.salesPassengers = [];
          this.enrichedPassengers = [];
          this.dataSource = [];
          this.flightDataSource.data = [];
          this.paxEntriesByFlightId.clear();
        },
      });
  }

  get showTable(): boolean {
    return !this.isLoading && this.dataSource !== null && this.dataSource.length > 0;
  }

  get showEmpty(): boolean {
    return !this.isLoading && this.dataSource !== null && this.dataSource.length === 0;
  }

  get flightCount(): number {
    return this.dataSource?.length ?? 0;
  }

  getPaxEntries(flight: FlightsModel): PaxEntry[] {
    return this.paxEntriesByFlightId.get(String(flight?.id ?? '')) ?? [];
  }

  get selectedFlight(): FlightsModel | null {
    if (!this.selectedFlightId) {
      return null;
    }

    return (
      this.flightDataSource.data.find(
        (flight) => String(flight?.id ?? '') === this.selectedFlightId
      ) ?? null
    );
  }

  get selectedFlightPaxEntries(): PaxEntry[] {
    if (!this.selectedFlight) {
      return [];
    }

    return this.getPaxEntries(this.selectedFlight);
  }

  get showPaxPanel(): boolean {
    return !!this.selectedFlight && this.selectedFlightPaxEntries.length > 0;
  }

  isFlightPaxSelected(flight: FlightsModel): boolean {
    return String(flight?.id ?? '') === this.selectedFlightId;
  }

  togglePaxPanel(flight: FlightsModel, event?: Event): void {
    event?.stopPropagation();

    const flightId = String(flight?.id ?? '');
    if (!flightId || !this.getPaxEntries(flight).length) {
      return;
    }

    this.selectedFlightId =
      this.selectedFlightId === flightId ? null : flightId;
  }

  closePaxPanel(): void {
    this.selectedFlightId = null;
  }

  getPaxCountLabel(count: number): string {
    const noun =
      count === 1 ? this.copy.paxCountSingular : this.copy.paxCountPlural;
    return `${count} ${noun}`;
  }

  getSelectedFlightLabel(): string {
    const flight = this.selectedFlight;
    if (!flight) {
      return this.copy.fallbackDash;
    }

    return flight.flightNumber || this.copy.fallbackDash;
  }

  formatAirport(value?: string | null): string {
    const normalized = (value ?? '').trim();
    if (!normalized || normalized.toUpperCase().includes('FALTAN DATOS')) {
      return this.copy.fallbackDash;
    }
    return normalized;
  }

  private rebuildPaxCache(): void {
    this.paxEntriesByFlightId.clear();

    for (const flight of this.flightDataSource.data) {
      this.paxEntriesByFlightId.set(
        String(flight.id),
        this.buildSortedPaxEntries(flight)
      );
    }
  }

  private buildSortedPaxEntries(flight: FlightsModel): PaxEntry[] {
    const entries = new Map<string, PaxEntry>();

    for (const link of this.enrichedPassengers) {
      if (!this.isPassengerOnFlight(link, flight)) {
        continue;
      }

      const name = this.resolvePassengerName(link);
      if (this.isInvalidDisplayName(name)) {
        continue;
      }

      const key = link.passengerId || link.Pax || name;
      entries.set(key, {
        name,
        sortKey: this.buildPassengerSortKey(link, name),
      });
    }

    return [...entries.values()].sort((left, right) =>
      left.sortKey.localeCompare(right.sortKey, 'es', {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }

  private buildPassengerSortKey(link: EnrichedPassengerLink, fallbackName: string): string {
    const detail =
      this.findSalesPassengerDetail(link) ??
      link.pasajero ??
      this.assignedPassengers.find((passenger) => passenger.id === link.passengerId);

    const sortKey = `${detail?.lastName ?? ''} ${detail?.firstName ?? ''}`.trim();
    return sortKey || fallbackName;
  }

  private findSalesPassengerDetail(link: PassengerFlightLink): PassengerDetail | undefined {
    return this.salesPassengers.find(
      (row) =>
        row.pasajero?.id === link.passengerId ||
        (!!link.Pax && row.Pax === link.Pax)
    )?.pasajero;
  }

  private isInvalidDisplayName(name: string): boolean {
    const normalized = name.trim().toUpperCase();
    return !normalized || normalized === '-' || normalized.includes('FALTAN DATOS');
  }

  private extractAssignedPassengers(response: any): PassengerDetail[] {
    return response?.getAssignedPassengers ?? [];
  }

  private extractSalesPassengers(response: any): SalesPassengerRow[] {
    return response?.getSalesPassengersByItinerary ?? [];
  }

  mergePassengerFlightLinks(itinerary: any): PassengerFlightLink[] {
    const byKey = new Map<string, PassengerFlightLink>();

    const addRows = (rows: PassengerFlightLink[] | null | undefined) => {
      for (const row of rows ?? []) {
        const key = row.passengerId || row.Pax || row.pasajero?.id || '';
        if (!key) {
          continue;
        }

        const flightIds = this.collectFlightIds(row);
        const existing = byKey.get(key);

        if (!existing) {
          byKey.set(key, {
            ...row,
            flightIds,
            pasajero: row.pasajero,
          });
          continue;
        }

        existing.flightIds = [...new Set([...(existing.flightIds ?? []), ...flightIds])];
        existing.pasajero = existing.pasajero ?? row.pasajero;
      }
    };

    addRows(itinerary?.lstPaX);

    for (const day of itinerary?.itinerarios ?? []) {
      for (const service of day?.services ?? []) {
        addRows(service?.lstPaX);
      }
    }

    return [...byKey.values()];
  }

  collectFlightIds(row: PassengerFlightLink): string[] {
    return [...new Set(row.flightIds ?? [])];
  }

  isPassengerOnFlight(pax: EnrichedPassengerLink, flight: FlightsModel): boolean {
    return this.matchesFlight(this.collectFlightIds(pax), flight);
  }

  matchesFlight(flightIds: string[] | null | undefined, flight: FlightsModel): boolean {
    const candidates = flightIds ?? [];
    if (!candidates.length) {
      return false;
    }

    const flightId = String(flight?.id ?? '');
    const flightNumber = String(flight?.flightNumber ?? '');
    const flightCode = String((flight as { flightCode?: string })?.flightCode ?? '');

    return candidates.some((linkedFlightId) => {
      const normalized = String(linkedFlightId);
      return (
        normalized === flightId ||
        (!!flightNumber && normalized === flightNumber) ||
        (!!flightCode && normalized === flightCode)
      );
    });
  }

  resolvePassengerName(pax: EnrichedPassengerLink): string {
    const salesName = this.formatPassengerName(this.findSalesPassengerDetail(pax));
    if (salesName !== this.copy.fallbackDash) {
      return salesName;
    }

    const embeddedName = this.formatPassengerName(pax.pasajero);
    if (embeddedName !== this.copy.fallbackDash) {
      return embeddedName;
    }

    const assignedPassenger = this.assignedPassengers.find(
      (passenger) => passenger.id === pax.passengerId
    );
    const assignedName = this.formatPassengerName(assignedPassenger);
    if (assignedName !== this.copy.fallbackDash) {
      return assignedName;
    }

    if (pax.Pax && !pax.Pax.startsWith('passenger-') && !pax.Pax.startsWith('tc-')) {
      return pax.Pax;
    }

    return this.copy.fallbackDash;
  }

  formatPassengerName(passenger?: PassengerDetail | null): string {
    if (!passenger) {
      return this.copy.fallbackDash;
    }

    const fullName = `${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`.trim();
    return fullName || this.copy.fallbackDash;
  }

  getStatusClass(status: string | null | undefined): string {
    const normalized = (status ?? '').trim().toLowerCase();
    if (!normalized || normalized === '-') {
      return 'status-default';
    }
    if (normalized.includes('confirm')) {
      return 'status-confirmed';
    }
    if (normalized.includes('pend')) {
      return 'status-pending';
    }
    if (normalized.includes('cancel')) {
      return 'status-cancelled';
    }
    return 'status-default';
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this.liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this.liveAnnouncer.announce('Sorting cleared');
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  getHourFromDate(date: string): string {
    return moment(date, 'HH:mm:ss').format('HH:mm');
  }
}
